import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type CommoditiesFormType, getCommoditiesFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/commoditiesFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CommodityDetailCommodityManufacturer,
  CommodityDetailIngotWeight,
  ContractProposalBaseInvestmentToolType,
  CreateContractProposalCommodityBodyInvestmentToolType,
  PatchContractProposalCommodityBodyInvestmentToolType,
} from "Infrastructure/ApiZfp/Schema";
import type {
  ContractProposalBaseSolutionHorizonType,
  CreateContractProposalCommodityBody,
  PatchContractProposalCommodityBody,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailCommodity } from "Infrastructure/ApiZfp/Schema/contractProposalDetailCommodity";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";

type Props = {
  readonly edit?: boolean;
  readonly partner?: string;
  readonly proposedSolutionId?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly initialData?: ContractProposalDetailCommodity | null;
};

export const CommoditiesForm = forwardRef<{}, Props>(({ partner, edit, proposedSolutionId, horizonType, onSuccess, initialData }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const partnerRef = useRef(partner);
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();

  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const brandOptions: Option[] = (
    Object.keys(CommodityDetailCommodityManufacturer) as Array<keyof typeof CommodityDetailCommodityManufacturer>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.GoalsFulfillment.BrandOptions[value]),
  }));

  const ingotWeightOptions: Option[] = (Object.keys(CommodityDetailIngotWeight) as Array<keyof typeof CommodityDetailIngotWeight>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.IngotWeightOptions[value]),
    }),
  );

  const form = useForm<CommoditiesFormType>({
    resolver: yupResolver(getCommoditiesFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: initialData.investmentToolType,
            partner: partner ?? "",
            investmentAmount: initialData.oneTimeAmount?.value ?? 0,
            horizon: initialData.currentSolutionHorizon ?? 0,
            appreciationRate: initialData.appreciationRate ?? 0,
            brand: initialData.commodityDetail?.commodityManufacturer as CommodityDetailCommodityManufacturer,
            ingotWeight: initialData.commodityDetail?.ingotWeight ?? CommodityDetailIngotWeight.weight_1g,
            ingotCount: initialData.commodityDetail?.ingotCount ?? 0,
            entryFee: initialData.entryFee ?? 0,
            regularAmount: initialData.regularAmount?.value ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            note: initialData.note ?? "",
          }
        : {
            investmentToolType: ContractProposalBaseInvestmentToolType.commodity,
            partner: partner ?? "",
          },
  });

  const methods = form;
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    trigger,
    setValue,
  } = methods;

  useEffect(() => {
    if (edit && initialData) {
      reset({
        investmentToolType: initialData.investmentToolType,
        partner: partnerRef.current ?? "",
        investmentAmount: initialData.oneTimeAmount?.value ?? 0,
        horizon: initialData.currentSolutionHorizon ?? 0,
        appreciationRate: initialData.appreciationRate ?? 0,
        brand: initialData.commodityDetail?.commodityManufacturer as CommodityDetailCommodityManufacturer,
        ingotWeight: initialData.commodityDetail?.ingotWeight as CommodityDetailIngotWeight,
        ingotCount: initialData.commodityDetail?.ingotCount ?? 0,
        entryFee: initialData.entryFee ?? 0,
        regularAmount: initialData.regularAmount?.value ?? 0,
        equities: initialData.assetAllocation?.equities ?? 0,
        bonds: initialData.assetAllocation?.bonds ?? 0,
        commodities: initialData.assetAllocation?.commodities ?? 0,
        realEstate: initialData.assetAllocation?.realEstate ?? 0,
        cash: initialData.assetAllocation?.cash ?? 0,
        other: initialData.assetAllocation?.others ?? 0,
        note: initialData.note ?? "",
      });
    }
  }, [edit, initialData, reset]);

  useEffect(() => {
    setValue("partner", partner ?? "", { shouldValidate: true });
  }, [partner, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (edit) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
    triggerValidation: () => {
      trigger();
    },
  }));

  const onCreate = (data: CommoditiesFormType) => {
    if (!horizonType) return;

    const parsedPartnerId = Number(data.partner);
    const payload: CreateContractProposalCommodityBody = {
      investmentToolType: CreateContractProposalCommodityBodyInvestmentToolType.commodity,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      entryFee: data.entryFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      oneTimeAmount: { value: data.investmentAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      commodityDetail: {
        commodityManufacturer: data.brand as CommodityDetailCommodityManufacturer,
        ingotWeight: data.ingotWeight as CommodityDetailIngotWeight,
        ingotCount: data.ingotCount ?? 0,
      },
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  const onEdit = (data: CommoditiesFormType) => {
    if (!proposedSolutionId) return;

    const parsedPartnerId = Number(data.partner);
    const payload: PatchContractProposalCommodityBody = {
      investmentToolType: PatchContractProposalCommodityBodyInvestmentToolType.commodity,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      entryFee: data.entryFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      oneTimeAmount: { value: data.investmentAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      commodityDetail: {
        commodityManufacturer: data.brand as CommodityDetailCommodityManufacturer,
        ingotWeight: data.ingotWeight as CommodityDetailIngotWeight,
        ingotCount: data.ingotCount ?? 0,
      },
    };

    updateMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  name="investmentAmount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.InvestmentAmount)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  name="regularAmount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                  inputEndAdornment={
                    <Box sx={{ width: 110 }}>
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Box>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="horizon"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Years)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="appreciationRate"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      %
                    </Typography>
                  }
                />
              </Grid>

              <Grid size={4}>
                <FormDropdown
                  name="brand"
                  options={brandOptions}
                  control={control}
                  labelEndAdornment={<StyledLabelAdornment />}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.BrandOptions.Title)}
                  errors={errors}
                />
              </Grid>
              <Grid size={4}>
                <FormDropdown
                  name="ingotWeight"
                  options={ingotWeightOptions}
                  control={control}
                  labelEndAdornment={<StyledLabelAdornment />}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.IngotWeightOptions.Title)}
                  errors={errors}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="ingotCount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.IngotCount)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Pieces)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                    },
                  ]}
                />
              </Grid>

              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="entryFee"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFee)}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      %
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <CharacteristicsAccordion<CommoditiesFormType>
              key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
              defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
            />
          </Box>

          <Divider />

          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                  placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
